import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { IconBrandWhatsapp } from '@tabler/icons-react';
import { ModuleInactive, waLink } from './FeatureLockedModal';
import { moduleForPath } from '@constants/modules';
import { useCompanyFeatures } from '@contexts/CompanyFeaturesContext';
import { Button } from '@/components/lumen/button';
import { useLanguage } from '@contexts/LanguageContext';

/**
 * Route-level counterpart of the sidebar locks: catches direct URL hits into
 * a disabled module and replaces the page with a locked state. Sits around
 * the dashboard <Outlet /> so individual routes need no changes.
 */
export default function ModuleGuard({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { isFeatureEnabled, isLoading } = useCompanyFeatures();

  const module = moduleForPath(pathname);

  // Fail-open while loading — never flash a lock at users whose modules are on.
  if (!module || isLoading || isFeatureEnabled(module.code)) {
    return <>{children}</>;
  }

  return <FeatureLockedPage moduleLabel={module.sidebarLabel} />;
}

function FeatureLockedPage({ moduleLabel }: { moduleLabel: string }) {
  const { t } = useLanguage();

  return (
    <div className="grid min-h-[60vh] place-items-center">
      <div className="flex max-w-[420px] flex-col items-center gap-4 px-4">
        <span
          aria-hidden
          className="flex size-14 items-center justify-center rounded-full bg-surface-2 text-fg-muted ring-1 ring-line"
        >
          <Lock className="size-7" />
        </span>
        <h3 className="text-center font-semibold text-base text-fg">{t('lumen.featureLock.title')}</h3>
        <p className="text-center text-fg-muted text-sm">
          <ModuleInactive template={t('lumen.featureLock.moduleInactive')} moduleLabel={moduleLabel} />{' '}
          {t('lumen.featureLock.contactAdmin')}
        </p>
        <Button asChild>
          <a href={waLink(moduleLabel)} target="_blank" rel="noopener noreferrer">
            <IconBrandWhatsapp aria-hidden />
            {t('lumen.featureLock.contactSales')}
          </a>
        </Button>
      </div>
    </div>
  );
}
